import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { EDSFiltersRequestBody } from '@/features/eds/types/api/requests/EDSFiltersRequestBody';
import { EdsSortingRequest } from '@/features/eds/types/api/requests/EdsSortingRequestBody';
import { DeleteProducerPayload, GetProducerListSuccessPayload, ProducerSliceState } from './types/ProducerSliceState';

export const initialPagination = {
  numberOfItems: 0,
  first: '1',
  last: undefined,
  previous: '0',
  next: '0',
  page: 1,
  perPage: 15,
};

const initialState: ProducerSliceState = {
  producerList: {
    listings: [],
    pagination: initialPagination,
  },
  produceGroupList: {
    listings: [],
    pagination: initialPagination,
  },
  statuses: {
    isLoading: false,
    isError: false,
  },
  searchQuery: '',
  savedFiltersForSending: null,
  savedSortingForSending: null,
};

export const producersSlice = createSlice({
  name: 'producers',
  initialState,
  reducers: {
    getProducerLists: () => {},
    searchProducerLists: (state, { payload }: PayloadAction<string>) => {
      state.searchQuery = payload;

      state.producerList.listings = [];
      state.producerList.pagination = { ...initialPagination };

      state.produceGroupList.listings = [];
      state.produceGroupList.pagination = { ...initialPagination };
    },
    getProducerListSuccess: (state, { payload }: PayloadAction<GetProducerListSuccessPayload>) => {
      const { shouldRewrite = true, data } = payload;

      state.producerList.listings = shouldRewrite
        ? [...data.listings]
        : [...state.producerList.listings, ...data.listings];

      state.producerList.pagination = data.pagination;

      state.statuses.isLoading = false;
      state.statuses.isError = false;
    },

    getProducerGroupListSuccess: (state, { payload }: PayloadAction<GetProducerListSuccessPayload>) => {
      const { shouldRewrite = true, data } = payload;

      state.produceGroupList.listings = shouldRewrite
        ? [...data.listings]
        : [...state.produceGroupList.listings, ...data.listings];

      state.produceGroupList.pagination = data.pagination;

      state.statuses.isLoading = false;
      state.statuses.isError = false;
    },
    incrementProducerListPage: (state) => {
      state.producerList.pagination.page++;
      state.statuses.isLoading = true;
    },
    decrementProducerListPage: (state) => {
      if (state.producerList.pagination.page > 1) {
        state.producerList.pagination.page--;
        state.statuses.isLoading = true;
      }
    },
    incrementProducerGroupListPage: (state) => {
      state.produceGroupList.pagination.page++;
      state.statuses.isLoading = true;
    },
    decrementProducerGroupListPage: (state) => {
      if (state.produceGroupList.pagination.page > 1) {
        state.produceGroupList.pagination.page--;
        state.statuses.isLoading = true;
      }
    },
    setLoadingProducerListData: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isLoading = payload;
    },
    getProducerListDataError: (state) => {
      state.statuses.isLoading = false;
      state.statuses.isError = true;
    },
    resetProducerLists: (state) => {
      state.producerList.listings = [];
      state.producerList.pagination = { ...initialPagination };

      state.produceGroupList.listings = [];
      state.produceGroupList.pagination = { ...initialPagination };

      state.searchQuery = '';
      state.statuses.isError = false;
      state.statuses.isLoading = false;
    },
    updateProducerListSavedFiltersForSending: (state, { payload }: PayloadAction<EDSFiltersRequestBody>) => {
      state.savedFiltersForSending = payload;
    },
    updateProducerListSavedSortingForSending: (state, { payload }: PayloadAction<EdsSortingRequest>) => {
      state.savedSortingForSending = payload;
    },
    deleteProducer: (_, _action: PayloadAction<DeleteProducerPayload>) => {},
  },
});

export const {
  setLoadingProducerListData,
  getProducerLists,
  getProducerGroupListSuccess,
  getProducerListSuccess,
  incrementProducerListPage,
  decrementProducerListPage,
  incrementProducerGroupListPage,
  decrementProducerGroupListPage,
  getProducerListDataError,
  searchProducerLists,
  resetProducerLists,
  updateProducerListSavedFiltersForSending,
  updateProducerListSavedSortingForSending,
  deleteProducer,
} = producersSlice.actions;

export const producersReducer = producersSlice.reducer;
export const producersReducerPath = producersSlice.reducerPath;
